/** Sovereign app: providers and the top-level routes (see router.ts for the route table). */
import { lazy, Suspense, useEffect } from 'react';
import { LazyMotion, MotionConfig } from 'motion/react';
import { useSession } from './net/session';
import { useRoute } from './router';
import { ToastProvider } from './ui/Toast';
import { Landing } from './screens/Landing';
import { JoinScreen } from './screens/student/JoinScreen';
import { NotFound } from './screens/NotFound';
import { LoadingScreen } from './ui/primitives';

// The game (student shell, desks, map) loads after sign-in, and teacher and projector code (placement,
// console) loads only on those devices, so the landing and join screens stay small on school Wi-Fi.
const StudentApp = lazy(() => import('./screens/student/StudentApp').then((m) => ({ default: m.StudentApp })));
const TeacherHome = lazy(() => import('./screens/teacher/TeacherHome').then((m) => ({ default: m.TeacherHome })));
const TeacherApp = lazy(() => import('./screens/teacher/TeacherApp').then((m) => ({ default: m.TeacherApp })));
// Single-player mode: the engine runs in the browser, so this chunk carries it (loaded only from #/solo).
const SoloApp = lazy(() => import('./solo/SoloApp').then((m) => ({ default: m.SoloApp })));
const ProjectorApp = lazy(() => import('./screens/projector/WorldStage').then((m) => ({ default: m.ProjectorApp })));

/** Animation features load in their own chunk after the first paint. */
const loadMotionFeatures = () => import('./motionFeatures').then((m) => m.default);

const TITLES: Record<string, string> = {
  '': 'Sovereign',
  join: 'Join your class · Sovereign',
  play: 'Sovereign',
  teacher: 'Teacher · Sovereign',
  projector: 'World Stage · Sovereign',
  solo: 'Play solo · Sovereign',
};

export function App() {
  const route = useRoute();
  const { student, teacher } = useSession();
  const top = route.parts[0] ?? '';
  const second = route.parts[1];

  useEffect(() => {
    document.title = TITLES[top] ?? 'Sovereign';
  }, [top]);

  let page;
  switch (top) {
    case '':
      page = <Landing />;
      break;
    case 'join':
      page = <JoinScreen key={second ?? ''} initialCode={second} />;
      break;
    case 'play':
      page = <StudentApp session={student} route={route} />;
      break;
    case 'teacher':
      if (!second || second === 'new') page = <TeacherHome initialMode="create" />;
      else if (second === 'signin') page = <TeacherHome initialMode="signin" />;
      else page = <TeacherApp session={teacher} route={route} />;
      break;
    case 'projector':
      page = <ProjectorApp session={teacher} />;
      break;
    case 'solo':
      page = <SoloApp route={route} />;
      break;
    default:
      page = <NotFound />;
  }

  // reducedMotion="user": with Reduce Motion on, every transform animation (slides, springs, scale) becomes
  // an instant change, and only opacity fades remain.
  return (
    <MotionConfig reducedMotion="user">
      <LazyMotion features={loadMotionFeatures} strict>
        <ToastProvider>
          <a className="skip-link" href="#main">
            Skip to content
          </a>
          <Suspense fallback={<LoadingScreen />}>{page}</Suspense>
        </ToastProvider>
      </LazyMotion>
    </MotionConfig>
  );
}
